/**
 * CopilotContext — holds the copilot's live state (the useCopilot hook + the
 * open/closed flag) at a persistent level ABOVE the per-route Layout.
 *
 * Why: Layout remounts on every in-app navigation (each <Switch> route has a
 * distinct key), which used to reset the copilot. Keeping the state here — the
 * provider is mounted once at the app shell (src/app/index.js) and never
 * remounts — lets the open state, active conversation and WS socket survive
 * navigation, while the VISIBLE FAB/window still mount only inside Layout (SMB
 * views), so the copilot never appears on enterprise/onboarding/upgrade
 * surfaces that carry a different access token.
 *
 * The provider itself opens nothing: useCopilot() only sets up state/refs and
 * connects on an explicit user action (open/send), which is only reachable from
 * the SMB-scoped FAB. No WS connect and no getMe happen here.
 */
import React, { createContext, useContext, useState } from 'react'
import { useCopilot, UseCopilot } from './useCopilot'

export interface CopilotContextValue {
  copilot: UseCopilot
  open: boolean
  setOpen: (open: boolean) => void
}

const CopilotContext = createContext<CopilotContextValue | null>(null)

export const CopilotProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const copilot = useCopilot()
  const [open, setOpen] = useState(false)
  return <CopilotContext.Provider value={{ copilot, open, setOpen }}>{children}</CopilotContext.Provider>
}

export const useCopilotContext = (): CopilotContextValue => {
  const ctx = useContext(CopilotContext)
  if (!ctx) throw new Error('useCopilotContext must be used within a CopilotProvider')
  return ctx
}
